<!-- src/views/manager/ManagerAddStaff.vue -->
<template>
  <div class="coredeck-fullwidth" style="margin-top: 18px">
    <div class="row" style="margin-bottom: 0">
      <div class="col s12">
        <!-- ADD / EDIT STAFF CARD -->
        <div class="card">
          <div class="card-content">
            <span class="card-title">Add / Edit Staff</span>

            <div
              class="grey-text text-darken-1"
              style="margin-top: -6px; font-size: 13px"
            >
              Create and update staff records used for rotas and wage summaries.
            </div>

            <div class="divider" style="margin: 14px 0"></div>

            <StaffDetailsManager
              ref="staffManager"
              :venueId="activeVenueId"
              :busy="busy || loading || !!deletingStaffId"
              @saved="loadStaff"
            />
          </div>
        </div>

        <!-- CURRENT STAFF LIST -->
        <div class="card" style="margin-top: 16px">
          <div class="card-content">
            <div style="display: flex; align-items: center; gap: 12px">
              <i class="material-icons">people</i>

              <span class="card-title" style="margin: 0"> Current Staff </span>

              <span
                class="new badge"
                data-badge-caption="staff"
                style="margin-left: auto"
              >
                {{ staff.length }}
              </span>
            </div>

            <div class="divider" style="margin: 14px 0"></div>

            <div v-if="loading" class="progress">
              <div class="indeterminate"></div>
            </div>

            <div v-else-if="!activeVenueId" class="grey-text text-darken-1">
              No venue selected.
            </div>

            <div v-else-if="!staff.length" class="grey-text text-darken-1">
              No staff saved yet.
            </div>

            <ul v-else class="staff-list">
              <li v-for="s in staff" :key="s.id" class="staff-row">
                <div class="staff-main">
                  <div class="staff-name">
                    {{ s.staff_name }}
                  </div>

                  <div class="staff-sub grey-text text-darken-1">
                    <span class="staff-email">
                      {{ s.email || "—" }}
                    </span>

                    <span class="dot">•</span>

                    <span class="staff-role">
                      {{ s.job_title || "—" }}
                    </span>
                  </div>
                </div>

                <div class="staff-kpis">
                  <div class="kpi">
                    <div class="kpi-label grey-text">Pay</div>

                    <div class="kpi-value">£{{ Number(s.pay_rate || 0).toFixed(2) }}</div>
                  </div>

                  <div class="kpi">
                    <div class="kpi-label grey-text">Active</div>

                    <div class="kpi-value">
                      <i
                        class="material-icons"
                        :class="s.active !== false ? 'green-text' : 'grey-text'"
                        style="vertical-align: middle"
                      >
                        {{ s.active !== false ? "check_circle" : "cancel" }}
                      </i>
                    </div>
                  </div>
                </div>

                <div class="staff-actions">
                  <a
                    class="btn-small"
                    :class="{
                      disabled: busy || loading || !!deletingStaffId,
                    }"
                    @click.prevent="onEditFromList(s)"
                  >
                    Edit
                  </a>

                  <a
                    href="#!"
                    class="btn-small"
                    :class="starterBtnClass(s)"
                    @click.prevent="onOpenStarterForms(s)"
                  >
                    {{ starterBtnLabel(s) }}
                  </a>

                  <a
                    class="btn-small red"
                    :class="{
                      disabled: busy || loading || deletingStaffId === s.id,
                    }"
                    @click.prevent="onDeleteFromList(s)"
                  >
                    {{ deletingStaffId === s.id ? "Deleting..." : "Delete" }}
                  </a>
                </div>
              </li>
            </ul>

            <div class="right-align" style="margin-top: 12px">
              <a href="#!" :class="{ disabled: loading }" @click.prevent="loadStaff">
                {{ loading ? "Refreshing..." : "Refresh staff list" }}
              </a>
            </div>
          </div>
        </div>

        <!-- STAFF TRAINING PROGRESS SUMMARY -->
        <StaffTrainingProgressSummary
          :key="activeVenueId"
          :venue-id="activeVenueId"
          :staff="staff"
        />

        <div v-if="error" class="card-panel red lighten-5 red-text text-darken-3">
          {{ error }}
        </div>
      </div>
    </div>

    <!-- STAFF STARTER FORMS MODAL -->
    <div
      id="staff-starter-forms-modal"
      ref="starterModalEl"
      class="modal modal-fixed-footer"
    >
      <div class="modal-content">
        <h5 style="margin: 0 0 12px 0">
          {{ starterModalTitle }}
        </h5>

        <div v-if="starterStaff && starterStaff.id">
          <EmployeeStarterFormsPanel
            :venueId="activeVenueId"
            :staffId="starterStaff.id"
            :staff="starterStaff"
            @completed="onStarterFormsCompleted"
            @saved="onStarterFormsCompleted"
          />
        </div>

        <div v-else class="grey-text">Select a staff member first.</div>
      </div>

      <div class="modal-footer">
        <a href="#!" class="modal-close btn-flat"> Close </a>
      </div>
    </div>
  </div>
</template>

<script>
import StaffDetailsManager from "@/components/rota/StaffDetailsManager.vue";
import EmployeeStarterFormsPanel from "@/components/rota/EmployeeStarterFormsPanel.vue";
import StaffTrainingProgressSummary from "@/components/training/StaffTrainingProgressSummary.vue";

import {
  listStaffDetails,
  deleteStaffDetail,
} from "@/scripts/sqlApiHelper/staffDetailsApi";

import { fsListAllVenues } from "@/scripts/firestore/venuesFs";

/* global M */

function asString(v) {
  return String(v ?? "").trim();
}

function lower(v) {
  return asString(v).toLowerCase();
}

function getActiveVenueFromStorage() {
  return asString(
    localStorage.getItem("coredeck_venue") || localStorage.getItem("venue") || ""
  );
}

function setActiveVenueToStorage(venueId) {
  const v = asString(venueId);

  localStorage.setItem("coredeck_venue", v);
  localStorage.setItem("venue", v);
}

function pickStarterCompleteFlag(s) {
  if (!s) return false;

  return (
    s.starter_forms_completed === true ||
    s.starterFormsCompleted === true ||
    s.starter_forms_complete === true ||
    s.starterFormsComplete === true
  );
}

export default {
  name: "ManagerAddStaff",

  components: {
    StaffDetailsManager,
    EmployeeStarterFormsPanel,
    StaffTrainingProgressSummary,
  },

  data() {
    return {
      loading: false,
      busy: false,
      deletingStaffId: "",
      error: null,
      staff: [],
      activeVenueId: "",
      _opsVenueHandler: null,

      // Modal state
      starterStaff: null,
      starterModalInst: null,
    };
  },

  computed: {
    starterModalTitle() {
      if (!this.starterStaff?.id) {
        return "Staff Starter Forms";
      }

      const done = pickStarterCompleteFlag(this.starterStaff);

      const name = this.starterStaff?.staff_name || "Staff";

      return done
        ? `Edit Staff Starter Forms – ${name}`
        : `Add Staff Starter Forms – ${name}`;
    },
  },

  async mounted() {
    const raw = getActiveVenueFromStorage();

    this.activeVenueId = await this.resolveCanonicalVenueId(raw);

    if (this.activeVenueId) {
      setActiveVenueToStorage(this.activeVenueId);
    }

    this._opsVenueHandler = async (e) => {
      const nextRaw = asString(e?.detail);

      const next = await this.resolveCanonicalVenueId(nextRaw);

      this.activeVenueId = next;

      if (next) {
        setActiveVenueToStorage(next);
      }

      this.starterStaff = null;

      await this.loadStaff();
    };

    window.addEventListener("ops-venue-changed", this._opsVenueHandler);

    this.$nextTick(() => {
      const el = this.$refs.starterModalEl;

      if (!el || !window.M?.Modal) {
        return;
      }

      const existing = window.M.Modal.getInstance(el);

      if (existing) {
        this.starterModalInst = existing;
      } else {
        this.starterModalInst = window.M.Modal.init(el, {
          dismissible: true,
        });
      }
    });

    await this.loadStaff();
  },

  beforeUnmount() {
    if (this._opsVenueHandler) {
      window.removeEventListener("ops-venue-changed", this._opsVenueHandler);

      this._opsVenueHandler = null;
    }

    try {
      this.starterModalInst?.destroy?.();
    } catch {
      // Ignore Materialize cleanup errors.
    }

    this.starterModalInst = null;
  },

  methods: {
    starterBtnLabel(s) {
      return pickStarterCompleteFlag(s) ? "Edit Starter Forms" : "Add Staff Details";
    },

    starterBtnClass(s) {
      const done = pickStarterCompleteFlag(s);

      return {
        disabled: this.busy || this.loading || !!this.deletingStaffId || !s?.id,

        teal: !done,
        "darken-1": !done,
        amber: done,
        "darken-2": done,
      };
    },

    onOpenStarterForms(s) {
      if (this.busy || this.loading || this.deletingStaffId || !s?.id) {
        return;
      }

      this.starterStaff = s;

      const el = this.$refs.starterModalEl;

      if (el && window.M?.Modal) {
        this.starterModalInst =
          this.starterModalInst ||
          window.M.Modal.getInstance(el) ||
          window.M.Modal.init(el, {
            dismissible: true,
          });

        this.starterModalInst?.open?.();
      }
    },

    onStarterFormsCompleted() {
      if (this.starterStaff) {
        this.starterStaff = {
          ...this.starterStaff,
          starter_forms_completed: true,
        };
      }

      const id = this.starterStaff?.id;

      if (id) {
        const i = this.staff.findIndex((x) => x?.id === id);

        if (i >= 0) {
          const cur = this.staff[i] || {};

          this.staff.splice(i, 1, {
            ...cur,
            starter_forms_completed: true,
          });
        }
      }

      window.M?.toast?.({
        html: "Starter forms saved",
      });
    },

    async resolveCanonicalVenueId(rawVenueId) {
      const raw = asString(rawVenueId);

      if (!raw) return "";

      let venues = [];

      try {
        venues = await fsListAllVenues();
      } catch (e) {
        console.warn("[ManagerAddStaff] fsListAllVenues failed; using raw venue id:", e);

        return raw;
      }

      const list = Array.isArray(venues) ? venues : [];

      const mapped = list
        .map((v) => {
          const id = asString(v?.id ?? v?.venue_id ?? "");

          const venue_id = asString(v?.venue_id ?? v?.id ?? "");

          const venue_name = asString(
            v?.venue_name ?? v?.venueName ?? v?.name ?? v?.label ?? ""
          );

          return {
            id,
            venue_id,
            venue_name,
          };
        })
        .filter((x) => !!x.id);

      const exact = mapped.find((x) => x.id === raw);

      if (exact) {
        return exact.id;
      }

      const rawL = lower(raw);

      const ci = mapped.find((x) => {
        return (
          lower(x.id) === rawL ||
          lower(x.venue_id) === rawL ||
          lower(x.venue_name) === rawL
        );
      });

      return ci ? ci.id : raw;
    },

    async loadStaff() {
      if (this.loading) return;

      try {
        this.error = null;

        if (!this.activeVenueId) {
          this.staff = [];
          return;
        }

        this.loading = true;

        const rows = await listStaffDetails(this.activeVenueId);

        this.staff = (rows || []).slice().sort((a, b) => {
          const an = String(a?.staff_name || "").toLowerCase();

          const bn = String(b?.staff_name || "").toLowerCase();

          return an.localeCompare(bn);
        });
      } catch (e) {
        this.error = e?.message || String(e);
      } finally {
        this.loading = false;
      }
    },

    onEditFromList(s) {
      if (this.busy || this.loading || this.deletingStaffId) {
        return;
      }

      const mgr = this.$refs.staffManager;

      if (!mgr?.onEdit) {
        window.M?.toast?.({
          html: "Staff editor not ready yet.",
        });

        return;
      }

      mgr.onEdit(s);

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      window.M?.toast?.({
        html: `Editing: ${s?.staff_name || "staff"}`,
      });
    },

    async onDeleteFromList(s) {
      const staffId = asString(s?.id);

      const staffName = asString(s?.staff_name || "this staff member");

      if (!this.activeVenueId || !staffId) {
        window.M?.toast?.({
          html: "Missing venue or staff ID",
        });

        return;
      }

      if (this.busy || this.loading || this.deletingStaffId) {
        return;
      }

      const ok = window.confirm(
        `Delete ${staffName}?\n\nThis will also delete their staff login code.`
      );

      if (!ok) return;

      this.error = null;
      this.busy = true;
      this.deletingStaffId = staffId;

      try {
        await deleteStaffDetail(this.activeVenueId, staffId);

        if (this.starterStaff?.id === staffId) {
          this.starterStaff = null;
        }

        const mgr = this.$refs.staffManager;

        if (mgr?.editingId === staffId && typeof mgr.onClear === "function") {
          mgr.onClear();
        }

        window.M?.toast?.({
          html: "Staff deleted",
        });
      } catch (e) {
        console.error("Delete staff failed:", e);

        this.error = e?.message || String(e);

        window.M?.toast?.({
          html: "Failed to delete staff",
        });
      } finally {
        this.busy = false;
        this.deletingStaffId = "";

        await this.loadStaff();
      }
    },
  },
};
</script>

<style scoped>
.coredeck-fullwidth {
  width: 100%;
  max-width: none;
  padding: 0 24px;
}

.staff-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.staff-row {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 14px;
  padding: 14px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  box-sizing: border-box;
}

.staff-main {
  flex: 1 1 260px;
  min-width: 0;
}

.staff-name {
  overflow: hidden;
  font-size: 16px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.staff-sub {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
  font-size: 12px;
}

.staff-email {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dot {
  opacity: 0.6;
}

.staff-kpis {
  display: flex;
  flex: 0 1 auto;
  min-width: 0;
  align-items: center;
  gap: 18px;
}

.kpi {
  min-width: 90px;
}

.kpi-label {
  font-size: 11px;
}

.kpi-value {
  margin-top: 2px;
  font-size: 14px;
  font-weight: 600;
}

.staff-actions {
  display: flex;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.staff-actions .btn-small {
  white-space: nowrap;
}

#staff-starter-forms-modal {
  width: 75% !important;
  max-width: 1100px;
}

@media (max-width: 900px) {
  .staff-kpis {
    order: 3;
    width: 100%;
  }

  .staff-actions {
    order: 2;
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 600px) {
  .coredeck-fullwidth {
    padding: 0 12px;
  }

  #staff-starter-forms-modal {
    width: 95% !important;
    max-width: none;
  }
}
</style>
